"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * Which side of the workspace you are looking at.
 *
 * Creator = you deliver. Collaborator = you booked the work.
 * Persisted so Overview, Collabs, and History stay on the same side.
 */
export type WorkspaceSide = "creator" | "brand";

export const WORKSPACE_SIDE_LABELS: Record<WorkspaceSide, string> = {
  creator: "Creator",
  brand: "Collaborator",
};

const STORAGE_KEY = "sendcollab.workspace-side";

type WorkspaceSideContextValue = {
  side: WorkspaceSide;
  setSide: (next: WorkspaceSide) => void;
  /** True when the signed-in account has collabs (or profiles) on both sides. */
  canToggle: boolean;
};

const WorkspaceSideContext = createContext<WorkspaceSideContextValue | null>(null);

function readStored(): WorkspaceSide {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value === "creator" || value === "brand") return value;
    // Older label schemes.
    if (value === "earning") return "creator";
    if (value === "spending" || value === "spent") return "brand";
  } catch {
    /* private mode */
  }
  return "creator";
}

function writeStored(side: WorkspaceSide) {
  try {
    localStorage.setItem(STORAGE_KEY, side);
  } catch {
    /* private mode */
  }
}

/** Persist side without requiring the provider (e.g. before a full-page redirect). */
export function rememberWorkspaceSide(side: WorkspaceSide) {
  writeStored(side);
}

export function WorkspaceSideProvider({
  children,
  canToggle,
  /** Preferred side when the account only has one — keeps a booker-only view honest. */
  forcedSide = null,
}: {
  children: ReactNode;
  canToggle: boolean;
  forcedSide?: WorkspaceSide | null;
}) {
  const [side, setSideState] = useState<WorkspaceSide>("creator");
  const [booted, setBooted] = useState(false);

  useEffect(() => {
    setSideState(readStored());
    setBooted(true);
  }, []);

  const setSide = useCallback((next: WorkspaceSide) => {
    setSideState(next);
    writeStored(next);
  }, []);

  const resolved: WorkspaceSide = !canToggle && forcedSide ? forcedSide : side;

  const value = useMemo(
    () => ({
      side: booted ? resolved : side,
      setSide,
      canToggle,
    }),
    [booted, resolved, side, setSide, canToggle],
  );

  return (
    <WorkspaceSideContext.Provider value={value}>{children}</WorkspaceSideContext.Provider>
  );
}

export function useWorkspaceSide(): WorkspaceSideContextValue {
  const ctx = useContext(WorkspaceSideContext);
  // Marketing TopNav (and other public shells) render without the provider.
  if (!ctx) {
    return { side: "creator", setSide: () => {}, canToggle: false };
  }
  return ctx;
}

/** Maps the global side onto a collab `yourRole`. */
export function sideToRole(side: WorkspaceSide): "creator" | "brand" {
  return side;
}
